import graphql__c926a820fcd65d70b4b6 from "./__generated__/MarketingEmailDetailsViewPreviewQuery.graphql.ts";
import graphql__59693ec5df5403c2f848 from "./__generated__/MarketingEmailDetailsView_marketingEmail.graphql.ts";
import graphql__0bad63c924615a4bc64d from "./__generated__/MarketingEmailDetailsView_query.graphql.ts";
import graphql__42a2dfaa48150423756e from "./__generated__/MarketingEmailDetailsViewAssignMailingListMutation.graphql.ts";
import graphql__4cf3dc0b8cae731767c1 from "./__generated__/MarketingEmailDetailsViewAssignTemplateMutation.graphql.ts";
import graphql__d3148e8c03e594740e7f from "./__generated__/MarketingEmailDetailsViewConfigureSenderMutation.graphql.ts";
import graphql__c11d8ab4c20eef7ab65b from "./__generated__/MarketingEmailDetailsViewMailingListsQuery.graphql.ts";
import graphql__ce33b7ad4c99acfb362d from "./__generated__/MarketingEmailDetailsViewTemplatesQuery.graphql.ts";import * as React from "react";
import {useState, useEffect, useRef} from "react";
import {graphql, fetchQuery} from "react-relay";
import {useFragment, useMutation, useRelayEnvironment} from "react-relay/hooks";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";
import {faSave} from "@fortawesome/pro-regular-svg-icons";
import ActionButton from "./presentational/button/ActionButton";
import AbstractCombobox from "./abstract/AbstractCombobox";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import {useErrorContext} from "../contexts/ErrorContext";
import type {MarketingEmailDetailsView_marketingEmail$key} from "./__generated__/MarketingEmailDetailsView_marketingEmail.graphql";
import type {MarketingEmailDetailsView_query$key} from "./__generated__/MarketingEmailDetailsView_query.graphql";
import type {MarketingEmailDetailsViewPreviewQuery} from "./__generated__/MarketingEmailDetailsViewPreviewQuery.graphql";

// Preview query for rendered email
const previewQuery = graphql__c926a820fcd65d70b4b6;

// Preview component that fetches rendered email from backend
const EmailPreview: React.FC<{
    marketingEmailId: string;
    language: string;
    hasTemplate: boolean;
}> = ({marketingEmailId, language, hasTemplate}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch preview when component mounts or dependencies change
    useEffect(() => {
        if (!hasTemplate) {
            setPreviewHtml('');
            return;
        }

        setIsLoading(true);
        setError(null);

        fetchQuery<MarketingEmailDetailsViewPreviewQuery>(
            relayEnvironment,
            previewQuery,
            {
                marketingEmailId,
                language,
            }
        ).subscribe({
            next: (data) => {
                if (data.marketingEmailPreview) {
                    setPreviewHtml(data.marketingEmailPreview.htmlBody);
                }
                setIsLoading(false);
            },
            error: () => {
                setError(intl.formatMessage({id: 'marketingEmail.preview.error'}));
                setIsLoading(false);
            },
        });
    }, [relayEnvironment, marketingEmailId, language, hasTemplate, intl]);

    // Update iframe content when preview changes
    useEffect(() => {
        if (iframeRef.current && previewHtml) {
            const doc = iframeRef.current.contentDocument;
            if (doc) {
                doc.open();
                doc.write(previewHtml);
                doc.close();
            }
        }
    }, [previewHtml]);

    if (!hasTemplate) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                {intl.formatMessage({id: 'marketingEmail.preview.noTemplate'})}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-white/75 flex items-center justify-center z-10">
                        <span className="text-gray-500 text-sm">{intl.formatMessage({id: "loading"})}</span>
                    </div>
                )}
                {error && (
                    <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                        <span className="text-red-500 text-sm">{error}</span>
                    </div>
                )}
                <iframe
                    ref={iframeRef}
                    title={intl.formatMessage({id: "marketingEmail.preview.title"})}
                    className="w-full min-h-[700px] border-0"
                    sandbox="allow-same-origin"
                />
            </div>
        </div>
    );
};

const MarketingEmailDetailsView: React.FC = () => {
    const intl = useIntl();
    const context: any = useOutletContext();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    const marketingEmail = useFragment<MarketingEmailDetailsView_marketingEmail$key>(
        graphql__59693ec5df5403c2f848,
        context.marketingEmail
    );

    // Note: query fragment kept for Relay store updates even if not directly used
    useFragment<MarketingEmailDetailsView_query$key>(
        graphql__0bad63c924615a4bc64d,
        context.query
    );

    const [commitAssignMailingList] = useMutation(graphql__42a2dfaa48150423756e);

    const [commitAssignTemplate] = useMutation(graphql__4cf3dc0b8cae731767c1);

    const [commitConfigureSender, isConfiguringSender] = useMutation(graphql__d3148e8c03e594740e7f);

    const isDraft = marketingEmail.status === 'DRAFT';

    // Local state for sender fields
    const [localSenderName, setLocalSenderName] = useState(marketingEmail.senderName || '');
    const [localSenderEmail, setLocalSenderEmail] = useState(marketingEmail.senderEmail || '');
    const [localReplyToEmail, setLocalReplyToEmail] = useState(marketingEmail.replyToEmail || '');

    // Track if sender fields have unsaved changes
    const hasSenderChanges =
        localSenderName !== (marketingEmail.senderName || '') ||
        localSenderEmail !== (marketingEmail.senderEmail || '') ||
        localReplyToEmail !== (marketingEmail.replyToEmail || '');

    // Sync local state when marketingEmail changes (e.g., after mutation response)
    useEffect(() => {
        setLocalSenderName(marketingEmail.senderName || '');
        setLocalSenderEmail(marketingEmail.senderEmail || '');
        setLocalReplyToEmail(marketingEmail.replyToEmail || '');
    }, [marketingEmail.senderName, marketingEmail.senderEmail, marketingEmail.replyToEmail]);

    // Get available languages from template variants
    const availableLanguages = marketingEmail.emailTemplate?.variants?.map(v => v.language) || [];
    const primaryLanguage = marketingEmail.emailTemplate?.primaryLanguage;

    // State for preview language selection - initialize to primary language or first available
    const [selectedPreviewLanguage, setSelectedPreviewLanguage] = useState<string>(() => {
        return marketingEmail.emailTemplate?.primaryLanguage
            || marketingEmail.emailTemplate?.variants?.[0]?.language
            || 'en'; // Ultimate fallback only if no template at all
    });

    // Update preview language when template changes (e.g., different template selected)
    useEffect(() => {
        if (availableLanguages.length > 0 && !availableLanguages.includes(selectedPreviewLanguage)) {
            // Fall back to primary language or first available
            setSelectedPreviewLanguage(primaryLanguage || availableLanguages[0]);
        }
    }, [availableLanguages, selectedPreviewLanguage, primaryLanguage]);

    const getLanguageLabel = (code: string) => {
        const labels: Record<string, string> = {
            'de': 'Deutsch',
            'en': 'English',
        };
        return labels[code] || code.toUpperCase();
    };

    // Data providers for comboboxes
    const mailingListDataProvider = AwesomeDebouncePromise((_searchTerm: string = '') => {
        return fetchQuery(relayEnvironment, graphql__c11d8ab4c20eef7ab65b, {}).toPromise().then((data: any) => {
            return data?.mailingLists || [];
        });
    }, 500);

    const templateDataProvider = AwesomeDebouncePromise((searchTerm: string = '') => {
        return fetchQuery(relayEnvironment, graphql__ce33b7ad4c99acfb362d, {searchTerm}).toPromise().then((data: any) => {
            return data?.availableEmailTemplates || [];
        });
    }, 500);

    const handleMailingListChange = (mailingList: any) => {
        commitAssignMailingList({
            variables: {
                input: {
                    id: marketingEmail.id,
                    mailingListIdentifier: mailingList?.mailingListIdentifier || null
                }
            },
            onError: () => {
                addError({
                    type: 'general',
                    message: intl.formatMessage({id: 'errors.failedToAssignMailingList'})
                });
            }
        });
    };

    const handleTemplateChange = (template: any) => {
        commitAssignTemplate({
            variables: {
                input: {
                    id: marketingEmail.id,
                    emailTemplateIdentifier: template?.emailTemplateIdentifier || null
                }
            },
            onError: () => {
                addError({
                    type: 'general',
                    message: intl.formatMessage({id: 'errors.failedToAssignTemplate'})
                });
            }
        });
    };

    const handleSaveSender = () => {
        // Validate: senderName and senderEmail are required
        if (!localSenderName.trim() || !localSenderEmail.trim()) {
            addError({
                type: 'validation',
                message: intl.formatMessage({id: 'errors.senderFieldsRequired'})
            });
            return;
        }

        commitConfigureSender({
            variables: {
                input: {
                    id: marketingEmail.id,
                    senderName: localSenderName.trim(),
                    senderEmail: localSenderEmail.trim(),
                    replyToEmail: localReplyToEmail.trim() || null
                }
            },
            onError: () => {
                addError({
                    type: 'general',
                    message: intl.formatMessage({id: 'errors.failedToConfigureSender'})
                });
            }
        });
    };

    return (
        <div className="space-y-6">
            {/* Top row: Two columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column: Template + Footer */}
                <div className="space-y-6">
                    {/* Template Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-3">
                            {intl.formatMessage({id: 'marketingEmail.section.template'})}
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {intl.formatMessage({id: 'marketingEmail.field.emailTemplate'})}
                                </label>
                                {isDraft ? (
                                    <AbstractCombobox
                                        initialValue={marketingEmail.emailTemplate}
                                        onValueChange={handleTemplateChange}
                                        getEntryId={(item: any) => item.id}
                                        getEntryLabel={(item: any) => item.name}
                                        dataProvider={templateDataProvider}
                                    />
                                ) : (
                                    <p className="text-sm text-gray-900">
                                        {marketingEmail.emailTemplate?.name || intl.formatMessage({id: 'marketingEmail.noTemplate'})}
                                    </p>
                                )}
                            </div>
                            {marketingEmail.emailTemplate?.emailLayout && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">
                                        {intl.formatMessage({id: 'marketingEmail.field.emailLayout'})}
                                    </label>
                                    <p className="text-sm text-gray-900">
                                        {marketingEmail.emailTemplate.emailLayout.name}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                {/* Right column: Recipients + Sender */}
                <div className="space-y-6">
                    {/* Recipients Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-3">
                            {intl.formatMessage({id: 'marketingEmail.section.recipients'})}
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {intl.formatMessage({id: 'marketingEmail.field.mailingList'})}
                                </label>
                                {isDraft ? (
                                    <AbstractCombobox
                                        initialValue={marketingEmail.mailingList}
                                        onValueChange={handleMailingListChange}
                                        getEntryId={(item: any) => item.id}
                                        getEntryLabel={(item: any) => item.name}
                                        getEntrySecondaryLabel={(item: any) => `${item.subscribedCount} ${intl.formatMessage({id: 'marketingEmail.subscribers'})}`}
                                        dataProvider={mailingListDataProvider}
                                    />
                                ) : (
                                    <p className="text-sm text-gray-900">
                                        {marketingEmail.mailingList?.name || intl.formatMessage({id: 'marketingEmail.noMailingList'})}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sender Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-gray-900">
                                {intl.formatMessage({id: 'marketingEmail.section.sender'})}
                            </h3>
                            {isDraft && (
                                <ActionButton
                                    icon={faSave}
                                    title={intl.formatMessage({id: 'button.save'})}
                                    onClick={handleSaveSender}
                                    disabled={!hasSenderChanges || isConfiguringSender}
                                />
                            )}
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {intl.formatMessage({id: 'marketingEmail.field.senderName'})}
                                </label>
                                {isDraft ? (
                                    <input
                                        type="text"
                                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-red-500 focus:ring-red-500 sm:text-sm"
                                        value={localSenderName}
                                        onChange={(e) => setLocalSenderName(e.target.value)}
                                    />
                                ) : (
                                    <p className="text-sm text-gray-900">{marketingEmail.senderName || '-'}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {intl.formatMessage({id: 'marketingEmail.field.senderEmail'})}
                                </label>
                                {isDraft ? (
                                    <input
                                        type="email"
                                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-red-500 focus:ring-red-500 sm:text-sm"
                                        value={localSenderEmail}
                                        onChange={(e) => setLocalSenderEmail(e.target.value)}
                                    />
                                ) : (
                                    <p className="text-sm text-gray-900">{marketingEmail.senderEmail || '-'}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {intl.formatMessage({id: 'marketingEmail.field.replyToEmail'})}
                                </label>
                                {isDraft ? (
                                    <input
                                        type="email"
                                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-red-500 focus:ring-red-500 sm:text-sm"
                                        value={localReplyToEmail}
                                        onChange={(e) => setLocalReplyToEmail(e.target.value)}
                                    />
                                ) : (
                                    <p className="text-sm text-gray-900">{marketingEmail.replyToEmail || '-'}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom row: Full-width Email Preview */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
                {/* Header with title */}
                <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900">
                        {intl.formatMessage({id: 'marketingEmail.preview.title'})}
                    </h3>
                </div>

                {/* Language tabs */}
                {availableLanguages.length > 0 && (
                    <div className="flex border-b border-gray-200 bg-white px-2">
                        {availableLanguages.map((language) => (
                            <button
                                key={language}
                                type="button"
                                onClick={() => setSelectedPreviewLanguage(language)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    selectedPreviewLanguage === language
                                        ? "border-red-600 text-red-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                            >
                                {getLanguageLabel(language)}
                            </button>
                        ))}
                    </div>
                )}

                {/* Preview content */}
                <div className="p-4">
                    <EmailPreview
                        marketingEmailId={marketingEmail.marketingEmailIdentifier}
                        language={selectedPreviewLanguage}
                        hasTemplate={!!marketingEmail.emailTemplate}
                    />
                </div>
            </div>
        </div>
    );
};

export default MarketingEmailDetailsView;
